'use client';

import React, { useRef, useMemo } from 'react';
import gsap from 'gsap';
import { Banner, type BannerProps } from '../landing';
import { CustomImage } from 'components/shared';
import { Heading, SubHeading2, Body2 } from './styleConstants';
import SplitText from './SplitText';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useMasterTimeline } from 'hooks/animation/useMasterTimeline';
import { useElementSize } from '../../hooks/dom/useElementSize';
import { BuyNowButton } from './ProductCards';
import styles from './HeroWithSplitBanner.module.css';

type CardData = {
  title: string;
  subtitle: string;
  imagePath: string;
};

type HeroWithSplitBannerProps = {
  bannerProps?: BannerProps;
  leftCard: CardData;
  rightCard: CardData;
};

const HeroWithSplitBanner: React.FC<HeroWithSplitBannerProps> = ({
  bannerProps,
  leftCard,
  rightCard,
}) => {
  const containerRef = useRef<HTMLElement>(null);
  const stickyWrapperRef = useRef<HTMLDivElement>(null);
  const leftBannerHalfRef = useRef<HTMLDivElement>(null);
  const rightBannerHalfRef = useRef<HTMLDivElement>(null);
  const leftImageRef = useRef<HTMLDivElement>(null);
  const rightImageRef = useRef<HTMLDivElement>(null);
  const leftTitleRef = useRef<HTMLDivElement>(null);
  const leftTextRef = useRef<HTMLDivElement>(null);
  const rightTitleRef = useRef<HTMLDivElement>(null);
  const rightTextRef = useRef<HTMLDivElement>(null);
  const [headingRef, headingSize] = useElementSize<HTMLHeadingElement>();

  const gsapContext = useGsapContext(containerRef);
  const { add } = useMasterTimeline();

  const mergedStyle = useMemo<React.CSSProperties>(
    () => ({
      width: '100%',
      height: '100%',
      ...(bannerProps?.style || {}),
    }),
    [bannerProps?.style],
  );

  const mergedSectionStyles = useMemo<React.CSSProperties>(
    () => ({
      width: '100%',
      height: '100%',
      minHeight: '100%',
      ...(bannerProps?.sectionStyles || {}),
    }),
    [bannerProps?.sectionStyles],
  );

  useIsomorphicLayoutEffect(() => {
    if (
      !stickyWrapperRef.current ||
      !leftBannerHalfRef.current ||
      !rightBannerHalfRef.current ||
      !leftImageRef.current ||
      !rightImageRef.current ||
      !leftTitleRef.current ||
      !leftTextRef.current ||
      !rightTitleRef.current ||
      !rightTextRef.current
    ) {
      return;
    }

    gsapContext.add(() => {
      const isMobile =
        typeof window !== 'undefined' && window.innerWidth <= 768;

      if (isMobile) {
        const tl = gsap.timeline({
          defaults: {
            duration: 1,
            ease: 'power2.easeInOut',
          },
          scrollTrigger: {
            immediateRender: false,
            trigger: stickyWrapperRef.current,
            start: 'top top',
            end: 'bottom bottom',
            scrub: 1,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          },
        });

        gsap.set(leftBannerHalfRef.current, {
          clipPath: 'inset(100% 0% 0% 0%)',
        });

        gsap.set(rightBannerHalfRef.current, {
          clipPath: 'inset(0% 0% 100% 0%)',
        });

        tl.to(
          [leftBannerHalfRef.current, rightBannerHalfRef.current],
          {
            clipPath: 'inset(0% 0% 0% 0%)',
            duration: 0.6,
            ease: 'power2.out',
          },
          '0',
        );

        tl.addLabel('bannersIn', 0.6);

        gsap.set(leftTitleRef.current, {
          x: -60,
          opacity: 0,
        });

        gsap.set(rightTitleRef.current, {
          x: -60,
          opacity: 0,
        });

        tl.to(
          [leftTitleRef.current, rightTitleRef.current],
          {
            x: 0,
            opacity: 1,
            duration: 0.4,
            ease: 'power2.out',
          },
          0.35,
        );

        gsap.set(leftTextRef.current, {
          x: 60,
          opacity: 0,
        });

        tl.to(
          leftTextRef.current,
          {
            x: 0,
            opacity: 1,
            duration: 0.4,
            ease: 'power2.out',
          },
          0.4,
        );

        gsap.set(rightTextRef.current, {
          x: 60,
          opacity: 0,
        });

        tl.to(
          rightTextRef.current,
          {
            x: 0,
            opacity: 1,
            duration: 0.4,
            ease: 'power2.out',
          },
          0.4,
        );

        tl.addLabel('complete', 0.95);

        tl.to(
          {},
          {
            duration: 0.2,
          },
          'complete',
        );

        add(tl, 'hero-split-banner-mobile');
      } else {
        const tl = gsap.timeline({
          defaults: {
            duration: 1,
            ease: 'power2.easeInOut',
          },
          scrollTrigger: {
            immediateRender: false,
            trigger: stickyWrapperRef.current,
            start: 'top top',
            end: 'bottom bottom',
            scrub: 1,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          },
        });

        gsap.set(leftBannerHalfRef.current, {
          clipPath: 'inset(0% 0% 100% 0%)',
        });

        gsap.set(rightBannerHalfRef.current, {
          clipPath: 'inset(100% 0% 0% 0%)',
        });

        tl.to(
          [leftBannerHalfRef.current, rightBannerHalfRef.current],
          {
            clipPath: 'inset(0% 0% 0% 0%)',
            duration: 0.6,
            ease: 'power2.out',
          },
          '0',
        );

        tl.addLabel('bannersIn', 0.6);

        gsap.set(leftTitleRef.current, {
          x: -60,
          opacity: 0,
        });

        gsap.set(rightTitleRef.current, {
          x: 60,
          opacity: 0,
        });

        tl.to(
          [leftTitleRef.current, rightTitleRef.current],
          {
            x: 0,
            opacity: 1,
            duration: 0.4,
            ease: 'power2.out',
          },
          0.35,
        );

        gsap.set(leftTextRef.current, {
          x: 60,
          opacity: 0,
        });

        tl.to(
          leftTextRef.current,
          {
            x: 0,
            opacity: 1,
            duration: 0.4,
            ease: 'power2.out',
          },
          0.4,
        );

        gsap.set(rightTextRef.current, {
          x: -60,
          opacity: 0,
        });

        tl.to(
          rightTextRef.current,
          {
            x: 0,
            opacity: 1,
            duration: 0.4,
            ease: 'power2.out',
          },
          0.4,
        );

        tl.addLabel('complete', 0.95);

        tl.to(
          {},
          {
            duration: 0.2,
          },
          'complete',
        );

        add(tl, 'hero-split-banner');
      }
    });
  }, [gsapContext, add]);

  return (
    <section className={styles.container} ref={containerRef}>
      <div className={styles.stickyWrapper} ref={stickyWrapperRef}>
        <div className={styles.stickyContainer}>
          <div className={styles.heroSection}>
            <div className={styles.headingWrapper}>
              <Heading ref={headingRef} $color="d_red" as="h1">
                <SplitText
                  text="DIESEL X ULTRAHUMAN"
                  tag="span"
                  splitType="chars"
                  delay={40}
                  duration={0.2}
                  ease="power3.out"
                  from={{ opacity: 0, y: 0 }}
                  to={{ opacity: 1, y: 0 }}
                />
              </Heading>
              <div
                className={styles.splitTextContainer}
                style={
                  {
                    '--stc-width': headingSize.width
                      ? `${headingSize.width - 10}px`
                      : 'auto',
                    '--stc-transform': headingSize.width
                      ? 'translateX(5px)'
                      : 'none',
                  } as React.CSSProperties
                }
              >
                <span className={styles.splitTextItem}>FASHION FORWARD</span>
                <span className={styles.splitTextItem}>FUTURE POWERED</span>
              </div>
            </div>

            <Banner
              {...bannerProps}
              style={mergedStyle}
              sectionStyles={mergedSectionStyles}
            />
          </div>

          <div className={styles.splitBannerSection}>
            <div className={styles.bannerHalf} ref={leftBannerHalfRef}>
              <div className={styles.imageWrapper} ref={leftImageRef}>
                <CustomImage
                  className={styles.styledImage}
                  src={leftCard.imagePath}
                  alt={leftCard.title}
                  fill
                  priority
                  sizes="50vw"
                />
              </div>
              <div className={styles.contentWrapper} data-is-left="true">
                <div
                  className={styles.titleWrapper}
                  ref={leftTitleRef}
                  data-is-left="true"
                >
                  <SubHeading2 $color="d_red">{leftCard.title}</SubHeading2>
                </div>
                <div
                  className={styles.textWrapper}
                  ref={leftTextRef}
                  data-is-left="true"
                >
                  <Body2 $color="white">{leftCard.subtitle}</Body2>
                </div>
                <div className={styles.buttonWrapper} data-is-left="true">
                  <BuyNowButton
                    href="/diesel-ultrahuman-ring/buy/"
                    aria-label="Buy Diesel Ultrahuman Ring now"
                  >
                    <span className={styles.buttonText}>BUY NOW</span>
                  </BuyNowButton>
                </div>
              </div>
            </div>

            <div className={styles.bannerHalf} ref={rightBannerHalfRef}>
              <div className={styles.imageWrapper} ref={rightImageRef}>
                <CustomImage
                  className={styles.styledImage}
                  src={rightCard.imagePath}
                  alt={rightCard.title}
                  fill
                  priority={true}
                  loading="eager"
                  sizes="50vw"
                />
              </div>
              <div className={styles.contentWrapper} data-is-left="false">
                <div
                  className={styles.titleWrapper}
                  ref={rightTitleRef}
                  data-is-left="false"
                >
                  <SubHeading2 $color="d_red">{rightCard.title}</SubHeading2>
                </div>
                <div
                  className={styles.textWrapper}
                  ref={rightTextRef}
                  data-is-left="false"
                >
                  <Body2 $color="white">{rightCard.subtitle}</Body2>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroWithSplitBanner;
